// Account Management
export interface LoginRequest {
  username?: string | null;
  password?: string | null;
  twoFactorCode?: string | null;
}

// Admin Receivable - for school payments list
export interface AdminReceivable {
  id: string;
  name: string;
  period: string;
  type: number;
  currencyId: number;
  currency: string;
  lastPaymentDate: string;
  description: string;
  createdAt: string;
  updatedAt?: string | null;
  paymentType: number;
  paymentTypeDescription: string;
  eventType: number;
  eventTypeDescription: string;
}

// School Receivable Detail - for /finance/receivables/{id}
export interface SchoolReceivableStudent {
  id: string;
  eventId: string;
  studentEventId: string;
  userId: string;
  user: string;
  studentId: string;
  student: string;
  amount: number;
  currency: number;
  status: number;
  statusDesc: string;
}

export interface SchoolReceivableDetail {
  id: string;
  name: string;
  period: string;
  type: number;
  paymentType: number;
  currencyId: number;
  currency: string;
  lastPaymentDate: string;
  description: string;
  createdAt: string;
  updatedAt?: string | null;
  student: SchoolReceivableStudent[];
}

export interface AdminReceivablesResponse {
  items: AdminReceivable[];
  pageNumber: number;
  totalPages: number;
  totalCount: number;
  orderBy: number;
  sortBy: string | null;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
}

// Create Receivable Request - for creating new school payments
export interface CreateReceivableRequest {
  name: string;
  period: string;
  type: number;
  currency: number;
  lastPaymentDate: string;
  description: string;
  students: Array<{
    studentId: string;
    price: number;
  }>;
}

export interface LoginResponse {
  success: boolean;
  message?: string;
  data?: {
    userId?: string;
    token?: string;
    refreshToken?: string;
    refreshTokenExpiresAt?: string;
    requiresTwoFactor?: boolean;
    message?: string;
  };
}

// Admin Login Types
export interface AdminLoginRequest {
  phoneCode: string;
  phoneNumber: string;
  password: string;
}

export interface AdminLoginResponse {
  userId: string;
  userStatus: number;
  token?: string;
  refreshToken?: string;
  refreshTokenExpiresAt?: string;
  passwordResetToken?: string; // Sadece MustChangePassword durumunda
}

export interface AdminChangePasswordRequest {
  userId: string;
  passwordResetToken: string;
  newPassword: string;
}

export interface UserMeResponse {
  success: boolean;
  data?: {
    userId: string;
    userName: string;
    fullName: string;
    email: string;
    role: string;
    userTypeItem: {
      id: string;
      userType: number;
    };
  };
}

export interface RegisterRequest {
  email?: string | null;
  phoneCode?: string | null;
  phoneNumber?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  password?: string | null;
  userType: UserType;
  userStatus: UserStatus;
}

export interface RefreshTokenRequest {
  expiredAccessToken?: string | null;
  refreshToken?: string | null;
}

export interface ChangePasswordRequest {
  password?: string | null;
  newPassword?: string | null;
}

export interface ForgotPasswordRequest {
  email?: string | null;
  phoneNumber?: string | null;
  phoneCode?: string | null;
}

export interface ResetPasswordRequest {
  phoneNumber?: string | null;
  phoneCode?: string | null;
  otpCode?: string | null;
  newPassword?: string | null;
}

// Education - Directory
export interface CreateSchoolBrandRequest {
  name?: string | null;
  email?: string | null;
  phoneCode?: string | null;
  phoneNumber?: string | null;
  webSite?: string | null;
  instagram?: string | null;
  facebook?: string | null;
}

export interface SchoolBrand {
  id: string;
  name: string;
  email?: string | null;
  phoneCode?: string | null;
  phoneNumber?: string | null;
  isActive: boolean;
  facebook?: string | null;
  instagram?: string | null;
  webSite?: string | null;
}

export interface SchoolBrandsResponse {
  data: {
    items: SchoolBrand[];
    pageNumber: number;
    totalPages: number;
    totalCount: number;
    orderBy: string;
    hasPreviousPage: boolean;
    hasNextPage: boolean;
  };
  success: boolean;
}

export interface UpdateSchoolBrandRequest {
  schoolBrandId: string;
  name?: string | null;
  email?: string | null;
  phoneCode?: string | null;
  phoneNumber?: string | null;
  isActive: boolean;
  webSite?: string | null;
  instagram?: string | null;
  facebook?: string | null;
}

export interface CreateSchoolRequest {
  name?: string | null;
  code?: string | null;
  schoolBrandId: string;
  address?: string | null;
  schoolType: SchoolType;
}

// New School Request for API v1
export interface CreateSchoolRequestV1 {
  name: string;
  code?: string | null;
  schoolBrandId: string | null;
  address: string;
  schoolType: number; // API expects number
  cityId: string;
  districtId: string;
  neighborhoodId: string;
  phoneCode: string;
  phoneNumber: string;
  email: string;
  isActive: boolean;
}

export interface UpdateSchoolRequestV1 {
  id: string;
  name: string;
  code: string;
  schoolBrandId: string | null;
  schoolManagerId?: string;
  address: string;
  isActive: boolean;
  schoolType: number; // API expects number
  cityId: string;
  districtId: string;
  neighborhoodId: string;
  phoneCode: string;
  phoneNumber: string;
  email: string;
}

// School interface for list
export interface School {
  id: string;
  name: string;
  code: string;
  schoolBrandId: string;
  schoolBrandName: string;
  address: string;
  schoolType: string; // Changed from number to string
  cityId: string;
  cityName: string;
  districtId: string;
  districtName: string;
  neighborhoodId: string;
  neighborhoodName: string;
  phoneCode: string;
  phoneNumber: string;
  email: string;
  isActive: boolean;
}

// Schools response interface
export interface SchoolsResponse {
  data: {
    items: School[];
    pageNumber: number;
    totalPages: number;
    totalCount: number;
    orderBy: string; // Changed from number to string
    sortBy: string;
    hasPreviousPage: boolean;
    hasNextPage: boolean;
  };
  success: boolean;
}

// School Type Enum
export enum SchoolType {
  Public = 1, // Devlet okulu
  Private = 2, // Özel okul
  Kindergarten = 3, // Anaokulu
  Primary = 4, // İlkokul
  Secondary = 5, // Ortaokul
  HighSchool = 6, // Lise
  College = 7, // Kolej
  Other = 8, // Diğer
}

// Location API Types
export interface City {
  id: string;
  name: string;
  code: string;
}

export interface CitiesResponse {
  data: {
    cities: City[];
  };
  success: boolean;
}

export interface District {
  id: string;
  name: string;
  code: string;
  cityId: string;
}

export interface DistrictsResponse {
  data: {
    districts: District[];
  };
  success: boolean;
}

export interface Neighborhood {
  id: string;
  name: string;
  code: string;
  districtId: string;
}

export interface NeighborhoodsResponse {
  data: {
    neighborhoods: Neighborhood[];
  };
  success: boolean;
}

export interface UpdateSchoolRequest {
  schoolId: string;
  name?: string | null;
  code?: string | null;
  schoolBrandId: string;
  schoolManagerId?: string | null;
  address?: string | null;
  isActive: boolean;
  schoolType: SchoolType;
}

export interface CreateClassroomRequest {
  name?: string | null;
  grade: number;
  section?: string | null;
  isActive: boolean;
  year: number;
  schoolId: string;
}

export interface UpdateClassroomRequest {
  classroomId: string;
  name?: string | null;
  grade: number;
  section?: string | null;
  isActive: boolean;
  year: number;
  schoolId: string;
}

// Education - People
export interface CreateSchoolManagerRequest {
  phoneCode: string;
  phoneNumber: string;
  email: string;
  firstName: string;
  lastName: string;
  password: string;
  schoolId: string;
  title: string;
  isPrimary?: boolean;
  requirePasswordResetOnFirstLogin: boolean;
}

// System User Types
export enum UserType {
  Parent = 0,
  SchoolManager = 1,
  ApplicationUser = 2,
  Admin = 3,
  SchoolUser = 4,
}

export enum SystemUserStatus {
  Inactive = 0,
  Active = 1,
  Suspended = 2,
  Deleted = 3,
}

export interface CreateSystemUserRequest {
  email: string;
  phoneCode: string;
  phoneNumber: string;
  firstName: string;
  lastName: string;
  password: string;
  userType: UserType;
  userStatus: SystemUserStatus;
  schoolId?: string; // Optional, only for SchoolManager
  twoFactorEnabled?: boolean;
  birthDate?: string;
}

export interface SystemUser {
  id: string;
  firstName: string;
  lastName: string;
  fullName: string;
  phoneCode: string;
  phoneNumber: string;
  email: string;
  birthDate: string;
  userType: string;
  userStatus: string;
  createdAt: string;
  updatedAt: string;
  lastModifiedBy: string;
  passwordModifiedDate: string;
  mustChangePassword: boolean;
  statusChangeReason: string;
  emailConfirmed: boolean;
  phoneNumberConfirmed: boolean;
  twoFactorEnabled: boolean;
  lockoutEnabled: boolean;
  accessFailedCount: number;
}

export interface UpdateSystemUserRequest {
  userId: string;
  firstName?: string;
  lastName?: string;
  birthDate?: string;
  userType?: string;
  userStatus?: string;
  twoFactorEnabled?: boolean;
  mustChangePassword?: boolean;
}

export interface SystemUsersResponse {
  items: SystemUser[];
  pageNumber: number;
  totalPages: number;
  totalCount: number;
  orderBy: string;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
}

export interface UpdateSchoolManagerRequest {
  id: string;
  userId: string;
  email: string;
  firstName: string;
  lastName: string;
  phoneCode: string;
  phoneNumber: string;
  schoolId?: string | null;
  title: string;
  isPrimary?: boolean | null;
  twoFactorEnabled?: boolean | null;
  mustChangePassword?: boolean | null;
  birthDate?: string | null;
  userStatus?: string | null;
}

// School Manager Edit Form Types
export interface SchoolManagerEditRequest {
  id: string;
  userId: string;
  firstName: string;
  lastName: string;
  title: string;
  isPrimary: boolean;
  twoFactorEnabled: boolean;
  userStatus: number;
}

export interface SchoolUserEditRequest {
  id: string;
  firstName: string;
  lastName: string;
  title: string;
  userStatus: number;
  roles: number;
}

export interface SchoolManagerDetails {
  id: string;
  userId: string;
  email: string;
  firstName: string;
  lastName: string;
  phoneCode: string;
  phoneNumber: string;
  schoolId: string;
  schoolName?: string;
  title: string;
  isPrimary: boolean;
  createdAt: string;
  updatedAt: string;
}

// School Manager for Users Table
export interface SchoolManager {
  id: string;
  userId: string;
  schoolId: string;
  title: string;
  isPrimary: boolean;
  fullName: string;
  email: string;
  phoneCode: string;
  phoneNumber: string;
  schoolName: string;
  status?: string; // User status as string
}

export interface SchoolManagersResponse {
  items: SchoolManager[];
  pageNumber: number;
  totalPages: number;
  totalCount: number;
  orderBy: string;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
}

export interface CreateStudentRequest {
  firstName: string;
  lastName: string;
  birthDate: string; // ISO date string
  gender: Gender;
  studentNumber: string;
  enrollmentDate: string; // ISO date string
  identityNumber: string;
  status: StudentStatus;
  schoolId: string;
  classroomId?: string; // optional
  parentIds: string[]; // array of parent IDs
}

export interface UpdateStudentRequest {
  studentId: string;
  firstName?: string | null;
  lastName?: string | null;
  birthDate: string; // ISO date-time
  gender: Gender;
  studentNumber?: string | null;
  phoneCode?: string | null;
  phoneNumber?: string | null;
  enrollmentDate?: string | null; // ISO date-time
  identityNumber?: string | null;
  status: StudentStatus;
  classroomId: string;
  parentId: string;
}

export interface CreateParentRequest {
  phoneCode?: string | null;
  phoneNumber?: string | null;
  email?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  identityNumber?: string | null;
  occupation?: string | null;
  birthDate?: string | null;
}

export interface UpdateParentRequest {
  parentId: string;
  identityNumber?: string | null;
  occupation?: string | null;
  relationType: ParentRelationType;
}

export interface ParentDetails {
  id: string;
  identityNumber: string;
  occupation: string;
  relationType: string;
  userId: string;
  firstName: string;
  lastName: string;
  phoneCode: string;
  phoneNumber: string;
  email: string;
  students: any[];
}

export interface ParentEditRequest {
  id: string;
  userId: string;
  firstName: string;
  lastName: string;
  identityNumber: string;
  occupation: string;
  relationType: ParentRelationType;
  birthDate?: string;
}

export interface Parent {
  id: string;
  userId: string;
  fullName: string;
  email: string;
  phoneCode: string;
  phoneNumber: string;
  identityNumber: string;
  occupation: string;
  relationType: string; // "Mother", "Father", "Guardian", "Other"
  studentCount: number;
}

export interface ParentsResponse {
  items: Parent[];
  pageNumber: number;
  totalPages: number;
  totalCount: number;
  orderBy: string;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
}

export interface AddParentToStudentRequest {
  studentId: string;
  parentId: string;
  parentRelationType: ParentRelationType;
}

export interface ParentDto {
  parentId: string;
  userId?: string;
  fullName: string;
  relationType: ParentRelationType;
  phoneCode: string;
  phoneNumber: string;
  email: string;
}

export interface GetStudentDetailsResponse {
  id: string;
  firstName: string;
  lastName: string;
  birthDate: string;
  gender: Gender;
  studentNumber: string;
  phoneCode: string;
  phoneNumber: string;
  enrollmentDate?: string;
  identityNumber: string;
  status: StudentStatus;
  schoolId?: string;
  schoolName?: string;
  classroomId?: string;
  classroomName?: string;
  parents: ParentDto[];
}

// Notifications
export interface SendOtpRequest {
  email?: string | null;
  phoneCode?: string | null;
  phoneNumber?: string | null;
}

export interface VerifyOtpRequest {
  email?: string | null;
  phoneCode?: string | null;
  phoneNumber?: string | null;
  code?: string | null;
}

// Common
export interface ListRequest {
  Q?: string;
  Page: number;
  Size: number;
  OrderBy: OrderByStatus;
  SortBy?: string;
}

// Enums
export enum Gender {
  Male = 1,
  Female = 2,
  Other = 3,
}

export enum UserStatus {
  Active = 1,
  Inactive = 2,
  RequiresVerification = 3,
  RequiredPassword = 4,
  LockedOut = 5,
  Suspended = 6,
  Disabled = 7,
  MustChangePassword = 8,
}

export enum StudentStatus {
  Active = 1,
  Graduated = 2,
  Transferred = 3,
  Suspended = 4,
  Left = 5,
  Deleted = 6,
}

export enum ParentRelationType {
  Mother = 1,
  Father = 2,
  Guardian = 3,
  Other = 4,
}

export enum OrderByStatus {
  Desc = 0,
  Asc = 1,
}

// Generic Response
export interface ApiResponse<T = any> {
  success: boolean;
  message?: string;
  data?: T;
}

// Legacy types for backward compatibility
export interface TFAVerificationRequest {
  username: string;
  tfaCode: string;
}

export interface TFAResendRequest {
  username: string;
}

// Student Management

export interface Student {
  id: string;
  studentId?: string; // API'den gelen studentId field'ı için
  firstName: string;
  lastName: string;
  birthDate: string;
  gender: Gender;
  studentNumber: string;
  phoneCode: string;
  phoneNumber: string;
  enrollmentDate?: string;
  identityNumber: string;
  status: StudentStatus;
  schoolId: string;
  schoolName?: string;
  classroomId?: string;
  className?: string;
  classroomName?: string; // API'den gelen classroomName field'ı için
  parentIds: string[];
  parents: ParentDto[];
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

// System Admin Types
export interface SystemAdmin {
  id: string;
  firstName: string;
  lastName: string;
  phoneCode: string;
  phoneNumber: string;
  email: string;
  userStatus: number;
  userType: number;
  createdAt: string;
  lastLoginAt?: string;
}

export interface SystemAdminsResponse {
  items: SystemAdmin[];
  pageNumber: number;
  totalPages: number;
  totalCount: number;
  orderBy: string;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
}

export interface CreateSystemAdminRequest {
  email: string;
  phoneCode: string;
  phoneNumber: string;
  firstName: string;
  lastName: string;
  password: string;
  userType: number;
  userStatus: number;
  twoFactorEnabled?: boolean;
  birthDate?: string;
}

// Event Types
export interface CreateEventRequest {
  schoolId: string;
  name: string;
  description: string;
  startsAt: string; // ISO date string
  endsAt: string; // ISO date string
  lastPaymentDate: string; // ISO date string
  eventType: EventType;
  paymentType: number;
  price: number;
  currency: Currency;
  quotaType: number; // 0 = unlimited, 1 = limited
  quota: number;
}

export interface UpdateEventRequest {
  id: string;
  name: string;
  description: string;
  startsAt: string; // ISO date string
  endsAt: string; // ISO date string
  lastPaymentDate: string; // ISO date string
  eventType: EventType;
  price: number;
  currency: Currency;
  quotaType: number; // 0 = unlimited, 1 = limited
  quota: number;
}

export interface Event {
  eventId: string;
  schoolId: string;
  schoolName: string;
  name: string;
  description: string;
  startsAt: string;
  endsAt: string;
  lastPaymentDate: string;
  eventType: EventType;
  paymentType: number;
  price: number;
  currency: Currency;
  quota: number;
  quotaTypeId?: number;     
  quotaType?: string;        
  joinQuota?: number;         
  activated?: boolean;    
  isActive?: boolean;
  createdAt?: string;       
  updatedAt?: string;       
  totalRegistrations?: number;
  confirmedRegistrations?: number;
  paidRegistrations?: number;
  pendigRegistrations?: number; 
  registrations?: EventRegistration[];
  agreements?: EventAgreement[];
}

export interface EventAgreement {
  eventAgreementDocumentId: string;
  agreementDocumentVersionId: string;
  agreementDocumentId: string;
  name: string;
  version: string;
  isLatest: boolean;
  assignedAt: string;
  createdAt: string;
  size: number;
}

export interface EventRegistration {
  registrationId: string;
  studentId: string;
  studentName: string;
  studentNumber: string;
  classroomName: string;
  status: string;
  statusDesc: string;
  paymentStatus: string;
  paymentStatusDesc: string;
  registeredAt: string;
  parents: ParentDto[];
}

export interface EventsResponse {
  data: {
    items: Event[];
    pageNumber: number;
    totalPages: number;
    totalCount: number;
    orderBy: string;
    sortBy: string;
    hasPreviousPage: boolean;
    hasNextPage: boolean;
  };
  success: boolean;
}

export enum EventType {
  Unknown = 0,
  Trip = 1,
  Vacation = 2,
  Concert = 3,
  Theater = 4,
  MuseumVisit = 5,
  Workshop = 6,
  Seminar = 7,
  Sports = 8,
  Competition = 9,
  Festival = 10,
  Graduation = 11,
  Orientation = 12,
  Picnic = 13,
  Charity = 14,
  Exam = 15,
  SchoolPayment = 16,
  Other = 99,
}

export enum Currency {
  TRY = 0,
  USD = 1,
  EUR = 2,
  GBP = 3,
}

// Payment Types
export enum ReceivableStatus {
  Pending = 0,
  PartiallyPaid = 1,
  Paid = 2,
  Cancelled = 3,
}

export interface Receivable {
  receivableId: string;
  eventId: string;
  studentEventId: string;
  studentId: string;
  studentName: string;
  title: string;
  description: string;
  amount: number;
  remainingAmount: number;
  currency: string;
  dueDate: string;
  status: string;
  createdAt: string;
}

export interface ReceivablesResponse {
  data: {
    items: Receivable[];
    pageNumber: number;
    totalPages: number;
    totalCount: number;
    orderBy: number;
    hasPreviousPage: boolean;
    hasNextPage: boolean;
  };
  success: boolean;
}

export interface SendReminderRequest {
  id: string;
  receivableId: string;
  sms: boolean;
  push: boolean;
  email: boolean;
  isAllUser: boolean;
}

export interface SendReminderResponse {
  success: boolean;
  message: string;
  smsSentCount: number;
  pushSentCount: number;
  emailSentCount: number;
  totalSentCount: number;
}

// Event Registration Types
export interface CancelEventRegistrationsRequest {
  id: string;
  studentEventIds: string[];
}

export interface SendEventReminderRequest {
  eventId: string;
  studentIds: string[];
  sms: boolean;
  push: boolean;
  email: boolean;
}

// Notification Types
export enum NotificationStatus {
  Pending = 0,
  Sent = 1,
  Read = 2,
  Deleted = 3,
  Failed = 4,
}

export interface Notification {
  id: string;
  userId: string;
  title: string;
  body: string;
  status: NotificationStatus;
  createdAt: string;
  readAt?: string;
  sentAt?: string;
}

export interface NotificationsResponse {
  items: Notification[];
  pageNumber: number;
  totalPages: number;
  totalCount: number;
  orderBy: number;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
}

export interface SendPushRequest {
  title: string;
  body: string;
  userIds: string[];
}

export interface NotificationSettings {
  inAppEnabled: boolean;
  pushEnabled: boolean;
  emailEnabled: boolean;
  smsEnabled: boolean;
  quietHoursStart?: string;
  quietHoursEnd?: string;
  timezone?: string;
  mutedCategoriesJson?: string;
}

export interface UpdateNotificationSettingsRequest {
  inAppEnabled?: boolean;
  pushEnabled?: boolean;
  emailEnabled?: boolean;
  smsEnabled?: boolean;
  quietHoursStart?: string;
  quietHoursEnd?: string;
  timezone?: string;
  mutedCategoriesJson?: string;
}

// Agreement Types
export enum AgreementType {
  User = 0,
  Event = 1,
}

export interface Agreement {
  id: string;
  name: string;
  agreementType: AgreementType;
  version: string;
  title: string;
  isForceUpdate: boolean;
  createdAt: string;
  updatedAt?: string;
}

export interface AgreementVersion {
  id: string;
  agreementId: string;
  version: string;
  content: string;
  createdAt: string;
  isLatest: boolean;
}

export interface AgreementsResponse {
  items: Agreement[];
  pageNumber: number;
  totalPages: number;
  totalCount: number;
  orderBy: number;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
}

export interface CreateAgreementRequest {
  name: string;
  agreementType: AgreementType;
  version: string;
  title: string;
  document: File;
  isForceUpdate?: boolean;
}

export interface UpdateAgreementRequest {
  id: string;
  name?: string;
  agreementType?: AgreementType;
  newVersion?: string;
  title?: string;
  content?: string;
  isForceUpdate?: boolean;
}

// Credit Card Types
export interface CreditCard {
  id: string;
  cardholderName: string;
  maskedCardNumber: string;
  expiryMonth: number;
  expiryYear: number;
  brand: string;
  isDefault: boolean;
  createdAt: string;
}

export interface CreditCardsResponse {
  items: CreditCard[];
  pageNumber: number;
  totalPages: number;
  totalCount: number;
  orderBy: number;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
}

export interface CreateCreditCardRequest {
  cardholderName: string;
  cardNumber: string;
  expiryMonth: number;
  expiryYear: number;
  cvv: string;
  isDefault?: boolean;
}

export interface InstallmentOption {
  installmentCount: number;
  installmentAmount: number;
  totalAmount: number;
  interestRate: number;
}

// Credit Application Types
export interface CreditApplication {
  applicationId: string;
  paymentIntentId: string;
  status: string;
  term: number;
  createdAt: string;
}

export interface StartApplicationRequest {
  paymentIntentId: string;
  identityNumber: string;
  phoneNumber: string;
  birthDate: string;
}

export interface VerifyApplicationRequest {
  applicationId: string;
  otpCode: string;
}

export interface ApplyApplicationRequest {
  applicationId: string;
  term: number;
}

// Payment Types
export enum PaymentIntentStatus {
  Created = 0,
  Expired = 1,
  PartiallyPaid = 2,
  Completed = 3,
  Refunded = 4,
  Cancelled = 5,
  Failed = 6,
}

export interface PaymentBasket {
  id: string;
  userId: string;
  totalAmount: number;
  currency: Currency;
  items: PaymentBasketItem[];
  createdAt: string;
}

export interface PaymentBasketItem {
  id: string;
  receivableId: string;
  title: string;
  amount: number;
  currency: Currency;
}

export interface PaymentIntent {
  id: string;
  basketId: string;
  amount: number;
  currency: Currency;
  status: PaymentIntentStatus;
  createdAt: string;
  expiresAt?: string;
}

export interface CardDetails {
  cardNumber: string;
  expiryMonth: string;
  expiryYear: string;
  cvv: string;
  cardHolderName: string;
  installmentCount?: number;
}

export interface PaymentMethod {
  id: string;
  provider: string;
  maskedPan: string;
  expiryMonth: number;
  expiryYear: number;
  brand: string;
  isDefault: boolean;
}

export interface CreatePaymentMethodRequest {
  provider: string;
  token: string;
  maskedPan?: string;
  expiryMonth?: number;
  expiryYear?: number;
  brand?: string;
  isDefault?: boolean;
}

// FairPay Types
export interface FairPayAmount {
  value: number;
  currency: string;
}

export interface FairPayCheckCardRequest {
  paymentSourceType: string;
  cardBin: string;
  amount: FairPayAmount;
  paymentOrigin?: string;
  force3DSecure?: boolean;
}

export interface FairPayRefundRequest {
  requestReference?: string;
  financialGroupId?: string;
  amount: FairPayAmount;
  originalTransactionId: number;
}

// Classroom Status
export enum ClassroomStatus {
  Inactive = 0,
  Active = 1,
  Archived = 2,
}

// FAQ Types
export interface FAQ {
  id: string;
  question: string;
  answer: string;
  order: number;
  isActive: boolean;
}

export interface FAQResponse {
  items: FAQ[];
  pageNumber: number;
  totalPages: number;
  totalCount: number;
  orderBy: number;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
}

// Student V2 API Types
export interface StudentParentV2 {
  parentId: string;
  parentRelationType: number;
  isPaymentManager: boolean;
}

export interface CreateStudentRequestV2 {
  firstName: string;
  lastName: string;
  birthDate: string;
  gender: number;
  studentNumber: string;
  enrollmentDate: string;
  identityNumber: string;
  status: number;
  schoolId: string;
  classroomId: string;
  parents: StudentParentV2[];
}

export interface UpdateStudentRequestV2 {
  id: string;
  firstName: string;
  lastName: string;
  birthDate: string;
  gender: number;
  studentNumber: string;
  enrollmentDate: string;
  identityNumber: string;
  status: number;
  schoolId: string;
  classroomId: string;
  parents: StudentParentV2[];
}
